import { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Plus, TrendUp, TrendDown, PaperPlaneRight, PencilSimple, Trash, X, Target, Clock,
  CheckCircle, XCircle, Spinner, ArrowsClockwise, Sparkle, BookOpen, Books, ArrowSquareOut,
  Warning, EnvelopeSimple, Megaphone, Briefcase
} from "@phosphor-icons/react";
import PortfolioPanel from "@/components/PortfolioPanel";

const OWNER_EMAIL = "whoshonk@gmail.com";

const SILLYS_BOOKS = [
  {
    title: "Rich Dad Poor Dad",
    author: "Robert Kiyosaki",
    blurb: "The classic that reframes how most people think about income, assets, and real wealth — and why your paycheck alone will never set you free.",
    tag: "Mindset · Wealth",
  },
  {
    title: "Zero to One",
    author: "Peter Thiel",
    blurb: "Notes on startups and building the future. Competition is for losers — monopoly, contrarian thinking, and creating something new from nothing.",
    tag: "Business · Contrarian",
  },
  {
    title: "How to Start an Airbnb Business",
    author: "Various",
    blurb: "A practical playbook for launching, scaling, and automating short-term rental income streams — from property selection to guest systems.",
    tag: "Cashflow · Real Estate",
  },
  {
    title: "Think and Grow Rich",
    author: "Napoleon Hill",
    blurb: "The original wealth-consciousness manual. Desire, faith, persistence, and the mastermind — the mental infrastructure behind every fortune.",
    tag: "Mindset · Classic",
  },
  {
    title: "The 48 Laws of Power",
    author: "Robert Greene",
    blurb: "A ruthless handbook of strategic behaviour studied by CEOs, athletes, and artists. Know the rules — use them, or defend against them.",
    tag: "Strategy · Power",
  },
  {
    title: "Reality Transurfing",
    author: "Vadim Zeland",
    blurb: "A cult-favourite framework for steering between parallel life paths. Less effort, less resistance — intention over force.",
    tag: "Consciousness · Esoteric",
  },
  {
    title: "Mind to Matter",
    author: "Dawson Church",
    blurb: "Science-backed exploration of how thought creates physical reality — neuroplasticity, epigenetics, and the biology of intention.",
    tag: "Science · Consciousness",
  },
  {
    title: "The Psychology of Money",
    author: "Morgan Housel",
    blurb: "Timeless lessons on wealth, greed, and happiness. It's not what you know about markets — it's how you behave when they test you.",
    tag: "Behavioural · Finance",
  },
  {
    title: "The Power of Not Reacting: How to Control Your Emotions",
    author: "Damon Zahariades",
    blurb: "Stoic-flavoured manual for emotional self-mastery. The gap between stimulus and response is where your edge — as trader and human — lives.",
    tag: "Stoicism · Self-control",
  },
  {
    title: "The Art of War",
    author: "Sun Tzu",
    blurb: "The 2,500-year-old military classic read by generals, founders, and traders alike. Patience, positioning, deception, and knowing when not to fight — the unchanging rules of any battlefield, including the market.",
    tag: "Strategy · Classic",
  },
];

function amazonLink(title, author) {
  const q = encodeURIComponent(`${title} ${author}`);
  return `https://www.amazon.com/s?k=${q}&i=stripbooks`;
}

// ---------- Broadcast email panel (owner only) ----------
function BroadcastPanel() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [forensics, setForensics] = useState(null);
  const [loadingForensics, setLoadingForensics] = useState(false);

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/goose/broadcast/preview-recipients`, { withCredentials: true });
      setPreview(r.data);
    } catch (err) {
      setPreview({ error: err.response?.data?.detail || "Couldn't load recipients" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadForensics = async () => {
    setLoadingForensics(true);
    try {
      const r = await axios.get(`${API}/goose/broadcast/last-send`, { withCredentials: true });
      setForensics(r.data);
    } catch (err) {
      setForensics({ error: err.response?.data?.detail || "Couldn't load forensics" });
    } finally {
      setLoadingForensics(false);
    }
  };

  // Auto-load preview on mount so the owner always sees who's about to get it
  useEffect(() => { loadPreview(); }, []);

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSendingTest(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/goose/broadcast/test`,
        { subject: subject.trim(), body: body.trim() },
        { withCredentials: true });
      setResult({ ok: true, test: true, ...r.data });
    } catch (err) {
      setResult({ ok: false, msg: err.response?.data?.detail || "Test send failed" });
    } finally {
      setSendingTest(false);
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/goose/broadcast`, { subject: subject.trim(), body: body.trim() }, { withCredentials: true });
      setResult({ ok: true, ...r.data });
      setSubject("");
      setBody("");
      loadPreview();
      // Auto-load forensics 8 seconds after the send so Resend has time to register events
      setTimeout(loadForensics, 8000);
    } catch (err) {
      setResult({ ok: false, msg: err.response?.data?.detail || "Failed to send" });
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  const charsLeft = 4000 - body.length;
  const ready = subject.trim().length > 0 && body.trim().length > 0 && !sending && !sendingTest;

  return (
    <div className="rounded-sm border border-amber-500/30 bg-zinc-900/40 p-5 max-w-2xl space-y-3" data-testid="goose-broadcast-panel">
      <div className="flex items-start gap-2">
        <Megaphone size={18} weight="fill" className="text-amber-400 mt-0.5" />
        <div>
          <h3 className="font-heading text-base font-bold text-zinc-100">Email Gold Members</h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Free-form note from Silly Goose. Goes to every Gold-tier user (and you) in their inbox.
          </p>
        </div>
      </div>

      {/* Recipient preview — shown BEFORE send so owner can sanity-check */}
      <div className="rounded-sm border border-zinc-800 bg-zinc-950/50 p-3" data-testid="broadcast-preview">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            Will be sent to {preview?.count ?? "…"} recipient{preview?.count === 1 ? "" : "s"}
          </span>
          <button
            onClick={loadPreview}
            disabled={previewLoading}
            className="text-[10px] text-zinc-500 hover:text-zinc-300 underline disabled:opacity-40"
            data-testid="broadcast-preview-refresh"
          >
            {previewLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {preview?.recipients?.length > 0 ? (
          <ul className="space-y-0.5">
            {preview.recipients.map((r) => (
              <li key={r.email} className="text-[11px] text-zinc-300 flex items-center gap-2">
                <span className="font-data text-amber-300/80">·</span>
                <span className="truncate">{r.email}</span>
                {r.tier === "gold" && (
                  <span className="text-[8px] px-1 py-0 bg-amber-900/30 text-amber-400 rounded-sm">GOLD</span>
                )}
                {r.email?.toLowerCase() === "whoshonk@gmail.com" && (
                  <span className="text-[8px] px-1 py-0 bg-zinc-800 text-zinc-400 rounded-sm">YOU</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] text-zinc-600 italic">
            {preview?.error || (previewLoading ? "Loading…" : "No Gold members yet — only you will get this email.")}
          </p>
        )}
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Subject</label>
        <input
          value={subject}
          onChange={e => setSubject(e.target.value.slice(0, 160))}
          placeholder="A note from Silly Goose..."
          maxLength={160}
          data-testid="broadcast-subject"
          className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
        />
        <p className="text-[9px] text-zinc-600 mt-0.5">{subject.length}/160</p>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Message</label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value.slice(0, 4000))}
          placeholder="Hey Gold members — markets are getting interesting. Here's what I'm watching..."
          rows={10}
          maxLength={4000}
          data-testid="broadcast-body"
          className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 resize-y leading-relaxed"
        />
        <p className="text-[9px] text-zinc-600 mt-0.5">{charsLeft} chars left · blank lines = paragraph breaks · plain text only (no HTML)</p>
      </div>

      {result && (
        <div
          className={`rounded-sm px-3 py-2 text-xs space-y-1 ${
            result.ok && (result.test || result.delivered === result.attempted)
              ? "border border-emerald-800/40 bg-emerald-900/20 text-emerald-300"
              : result.ok
              ? "border border-amber-800/40 bg-amber-900/20 text-amber-200"
              : "border border-red-800/40 bg-red-900/20 text-red-300"
          }`}
          data-testid="broadcast-result"
        >
          <div className="flex items-start gap-2">
            {result.ok ? <CheckCircle size={14} weight="fill" className="shrink-0 mt-0.5" /> : <XCircle size={14} weight="fill" className="shrink-0 mt-0.5" />}
            <span>
              {result.ok && result.test
                ? `Test sent to ${result.sent_to} — check your inbox (and Spam/Promotions if not in Primary).`
                : result.ok
                ? `Sent ${result.delivered}/${result.attempted}. Recipients should check Spam/Promotions if not in Primary. Use "Check delivery status" below to see Gmail's actual response per recipient.`
                : `Failed: ${result.msg}`}
            </span>
          </div>
          {result.ok && result.failed?.length > 0 && (
            <ul className="ml-6 mt-1 space-y-0.5">
              {result.failed.map((f, i) => (
                <li key={i} className="text-[10px] text-red-300">
                  · <span className="font-data">{f.email}</span> — {f.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!confirming ? (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={sendTest}
            disabled={!ready}
            data-testid="broadcast-test-send"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sendingTest ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneRight size={12} weight="bold" />}
            {sendingTest ? "Sending..." : "Send test to me only"}
          </button>
          <button
            onClick={() => setConfirming(true)}
            disabled={!ready}
            data-testid="broadcast-send"
            className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-sm bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <PaperPlaneRight size={12} weight="bold" /> Send to {preview?.count ?? "Gold members"}{preview?.count != null ? ` recipient${preview.count === 1 ? "" : "s"}` : ""}
          </button>
        </div>
      ) : (
        <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[12px] text-amber-200">
            Send this email to {preview?.count ?? "all"} recipient{preview?.count === 1 ? "" : "s"}?
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setConfirming(false)} className="text-[11px] px-3 py-1.5 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
            <button
              onClick={send}
              disabled={sending}
              data-testid="broadcast-confirm"
              className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-sm bg-amber-500/30 border border-amber-500/60 text-amber-100 hover:bg-amber-500/40 disabled:opacity-50"
            >
              {sending ? <Spinner size={11} className="animate-spin" /> : <PaperPlaneRight size={11} weight="bold" />}
              {sending ? "Sending..." : "Yes, send"}
            </button>
          </div>
        </div>
      )}

      {/* Forensics: per-recipient Gmail-side delivery status from Resend */}
      <div className="border-t border-zinc-800/60 pt-3 mt-2" data-testid="broadcast-forensics">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            Last broadcast delivery status
          </span>
          <button
            onClick={loadForensics}
            disabled={loadingForensics}
            className="text-[10px] text-amber-400 hover:text-amber-300 underline disabled:opacity-40"
            data-testid="forensics-refresh"
          >
            {loadingForensics ? "Loading…" : "Check delivery status"}
          </button>
        </div>
        {forensics?.last_send ? (
          <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3 space-y-1.5">
            <div className="text-[11px] text-zinc-300">
              <span className="font-data">"{forensics.last_send.subject}"</span>
              <span className="text-zinc-500 ml-2">
                · {forensics.last_send.delivered}/{forensics.last_send.attempted} sent
                · {new Date(forensics.last_send.sent_at).toLocaleString()}
              </span>
            </div>
            <ul className="space-y-0.5">
              {(forensics.events || []).map((e, i) => {
                const event = e.last_event || "—";
                const isGood = ["delivered", "opened", "clicked"].includes(event);
                const isBad = ["bounced", "complained", "delivery_delayed", "failed"].includes(event);
                return (
                  <li key={i} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-zinc-300 truncate">{e.email}</span>
                    <span className={`text-[10px] font-data px-1.5 py-0 rounded-sm ${
                      isGood ? "bg-emerald-900/30 text-emerald-400"
                        : isBad ? "bg-red-900/30 text-red-400"
                        : "bg-zinc-800 text-zinc-500"
                    }`}>
                      {event}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-[10px] text-zinc-600 italic mt-2 leading-relaxed">
              <span className="text-emerald-400">delivered</span> = Gmail SMTP accepted it. If recipient still can't see it,
              the message is in their <span className="text-amber-300">Spam, Promotions, or All Mail</span> folder.
              Have them search Gmail for <span className="font-data text-amber-300">from:silly@thegoldennest.io</span>.
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-zinc-600 italic">
            {forensics?.message || "No broadcasts logged yet — send one and click \"Check delivery status\" after 10 seconds."}
          </p>
        )}
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  active: { label: "Active", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Sparkle },
  hit_target: { label: "T1 Hit", cls: "bg-emerald-500/25 text-emerald-200 border-emerald-500/50", icon: CheckCircle },
  hit_target2: { label: "T2 Hit", cls: "bg-amber-500/25 text-amber-200 border-amber-500/50", icon: CheckCircle },
  stopped_out: { label: "Stopped", cls: "bg-red-500/15 text-red-300 border-red-500/30", icon: XCircle },
  closed: { label: "Closed", cls: "bg-zinc-800 text-zinc-400 border-zinc-700", icon: XCircle },
};

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n.toFixed(n >= 1 ? 2 : 6).replace(/\.?0+$/, "");
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function CallCard({ call, isOwner, onEdit, onDelete }) {
  const up = call.direction === "long";
  const s = STATUS_BADGE[call.status] || STATUS_BADGE.active;
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-900/50 p-3.5 hover:border-amber-500/30 transition-colors" data-testid={`goose-call-${call.id}`}>
      <div className="flex items-start gap-2 flex-wrap mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-data text-sm font-bold text-zinc-100">{call.symbol}</span>
            <span className="text-[10px] text-zinc-500">· {call.asset_name}</span>
            <Badge className="text-[8px] h-4 px-1.5 py-0 border-0 rounded-sm bg-zinc-800 text-zinc-400">{call.asset_type.toUpperCase()}</Badge>
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge className={`text-[9px] h-4 px-1.5 py-0 border rounded-sm ${up ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}`}>
              {up ? <TrendUp size={9} weight="bold" className="inline mr-0.5" /> : <TrendDown size={9} weight="bold" className="inline mr-0.5" />}
              {call.direction.toUpperCase()}
            </Badge>
            <Badge className="text-[9px] h-4 px-1.5 py-0 border border-amber-500/30 rounded-sm bg-amber-500/10 text-amber-300">
              <Clock size={9} className="inline mr-0.5" /> {call.timeframe.toUpperCase()}-TERM
            </Badge>
            <Badge className={`text-[9px] h-4 px-1.5 py-0 border rounded-sm ${s.cls}`}>
              {s.label}
            </Badge>
            {call.pnl_pct !== null && call.pnl_pct !== undefined && (
              <Badge className={`text-[9px] h-4 px-1.5 py-0 border-0 rounded-sm font-data ${call.pnl_pct >= 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                {call.pnl_pct >= 0 ? "+" : ""}{call.pnl_pct.toFixed(2)}%
              </Badge>
            )}
          </div>
        </div>
        {isOwner && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onEdit(call)} data-testid={`goose-edit-${call.id}`} className="p-1 text-zinc-500 hover:text-amber-300" title="Edit"><PencilSimple size={12} /></button>
            <button onClick={() => onDelete(call)} data-testid={`goose-delete-${call.id}`} className="p-1 text-zinc-500 hover:text-red-400" title="Delete"><Trash size={12} /></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[10px]">
        <div><span className="text-zinc-500">Entry</span><p className="font-data text-zinc-200">${fmt(call.entry)}</p></div>
        <div><span className="text-zinc-500">Target</span><p className="font-data text-emerald-300">${fmt(call.target)}</p></div>
        <div><span className="text-zinc-500">Target 2</span><p className="font-data text-emerald-300/80">${fmt(call.target2)}</p></div>
        <div><span className="text-zinc-500">Stop</span><p className="font-data text-red-300">${fmt(call.stop_loss)}</p></div>
      </div>

      {call.rationale && (
        <div className="mt-2.5 p-2 rounded-sm bg-zinc-950/50 border border-zinc-800/60 text-[11px] text-zinc-300 leading-relaxed">
          {call.rationale}
        </div>
      )}

      <p className="text-[9px] text-zinc-600 mt-2">Posted {relativeTime(call.created_at)}{call.updated_at !== call.created_at && <> · updated {relativeTime(call.updated_at)}</>}</p>
    </div>
  );
}

function CallEditor({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    symbol: initial?.symbol || "",
    asset_name: initial?.asset_name || "",
    asset_type: initial?.asset_type || "crypto",
    direction: initial?.direction || "long",
    timeframe: initial?.timeframe || "short",
    entry: initial?.entry ?? "",
    target: initial?.target ?? "",
    target2: initial?.target2 ?? "",
    stop_loss: initial?.stop_loss ?? "",
    rationale: initial?.rationale || "",
    status: initial?.status || "active",
    pnl_pct: initial?.pnl_pct ?? "",
  }));
  const [notify, setNotify] = useState(true);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isEdit = !!initial?.id;

  const submit = (e) => {
    e.preventDefault();
    // Convert blanks to null for numeric fields
    const numeric = ["entry", "target", "target2", "stop_loss", "pnl_pct"];
    const payload = { ...form };
    numeric.forEach(k => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
    // Only send notify flag on NEW calls (edits never broadcast anyway)
    if (!isEdit) payload.notify = notify;
    onSave(payload);
  };

  return (
    <form onSubmit={submit} className="rounded-sm border border-amber-500/40 bg-zinc-900/70 p-4 space-y-3" data-testid="goose-call-editor">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-amber-300">{isEdit ? "Edit Call" : "New Call"}</p>
        <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-zinc-300"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-[10px] text-zinc-500">Symbol
          <input value={form.symbol} onChange={e => set("symbol", e.target.value.toUpperCase())} required maxLength={20} data-testid="goose-field-symbol" className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 uppercase mt-0.5" />
        </label>
        <label className="text-[10px] text-zinc-500 col-span-1 sm:col-span-2">Asset Name
          <input value={form.asset_name} onChange={e => set("asset_name", e.target.value)} maxLength={80} placeholder="e.g. Bitcoin" className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5" />
        </label>
        <label className="text-[10px] text-zinc-500">Type
          <select value={form.asset_type} onChange={e => set("asset_type", e.target.value)} className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5">
            <option value="crypto">Crypto</option><option value="stock">Stock</option>
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-[10px] text-zinc-500">Direction
          <select value={form.direction} onChange={e => set("direction", e.target.value)} data-testid="goose-field-direction" className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5">
            <option value="long">Long</option><option value="short">Short</option>
          </select>
        </label>
        <label className="text-[10px] text-zinc-500">Timeframe
          <select value={form.timeframe} onChange={e => set("timeframe", e.target.value)} data-testid="goose-field-timeframe" className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5">
            <option value="short">Short-term</option><option value="long">Long-term</option>
          </select>
        </label>
        <label className="text-[10px] text-zinc-500">Status
          <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5">
            <option value="active">Active</option>
            <option value="hit_target">T1 Hit</option>
            <option value="hit_target2">T2 Hit</option>
            <option value="stopped_out">Stopped Out</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="text-[10px] text-zinc-500">P&amp;L %
          <input type="number" step="0.01" value={form.pnl_pct} onChange={e => set("pnl_pct", e.target.value)} className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5" />
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-[10px] text-zinc-500">Entry
          <input type="number" step="any" value={form.entry} onChange={e => set("entry", e.target.value)} data-testid="goose-field-entry" className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5" />
        </label>
        <label className="text-[10px] text-zinc-500">Target
          <input type="number" step="any" value={form.target} onChange={e => set("target", e.target.value)} className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5" />
        </label>
        <label className="text-[10px] text-zinc-500">Target 2
          <input type="number" step="any" value={form.target2} onChange={e => set("target2", e.target.value)} className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5" />
        </label>
        <label className="text-[10px] text-zinc-500">Stop-loss
          <input type="number" step="any" value={form.stop_loss} onChange={e => set("stop_loss", e.target.value)} className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100 mt-0.5" />
        </label>
      </div>
      <label className="text-[10px] text-zinc-500">Rationale
        <textarea value={form.rationale} onChange={e => set("rationale", e.target.value.slice(0, 2000))} rows={3} placeholder="Why this call? Technicals, fundamentals, catalysts..." data-testid="goose-field-rationale" className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-200 mt-0.5 resize-none" />
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="submit" disabled={saving} data-testid="goose-call-save" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 disabled:opacity-50">
          {saving ? <Spinner size={11} className="animate-spin" /> : <CheckCircle size={11} />} {isEdit ? "Save" : "Publish"}
        </button>
        <button type="button" onClick={onCancel} className="text-xs px-3 py-1.5 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
        {!isEdit && (
          <label className="inline-flex items-center gap-1.5 text-[10px] text-zinc-400 cursor-pointer select-none ml-auto" title="Uncheck to publish silently — no email to Gold members">
            <input
              type="checkbox"
              checked={notify}
              onChange={e => setNotify(e.target.checked)}
              data-testid="goose-field-notify"
              className="accent-amber-500 h-3 w-3"
            />
            <span>Email Gold members</span>
          </label>
        )}
      </div>
    </form>
  );
}

function GooseChat({ isOwner, me }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/goose/chat?limit=100`, { withCredentials: true });
      setMessages(prev => {
        const next = r.data.messages || [];
        if (prev.length === next.length && prev[prev.length - 1]?.id === next[next.length - 1]?.id) return prev;
        return next;
      });
    } catch (err) {
      console.error("Failed to fetch goose chat:", err);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    try {
      await axios.post(`${API}/goose/chat`, { message: text }, { withCredentials: true });
      await fetchMessages();
    } catch (err) {
      console.error("Failed to send goose chat message:", err);
    }
    setSending(false);
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/goose/chat/${id}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear ALL Silly Goose chat history? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/goose/chat`, { withCredentials: true });
      setMessages([]);
    } catch (err) {
      console.error("Failed to clear chat:", err);
      alert(err.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="rounded-sm border border-amber-500/25 bg-zinc-900/40 flex flex-col" style={{ height: 480 }} data-testid="goose-chat">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800/60">
        <span className="text-base">🪿</span>
        <p className="text-xs font-medium text-amber-300">Silly Goose Chat</p>
        <Badge className="text-[8px] h-4 px-1.5 py-0 border-0 rounded-sm bg-emerald-500/15 text-emerald-300">LIVE</Badge>
        <span className="text-[10px] text-zinc-500 ml-auto">{messages.length} msgs</span>
        {isOwner && messages.length > 0 && (
          <button
            onClick={clearAll}
            data-testid="goose-chat-clear"
            title="Clear all chat history (owner only)"
            className="inline-flex items-center gap-1 text-[9px] text-zinc-500 hover:text-red-400 transition-colors px-1.5 py-0.5 rounded-sm border border-zinc-800 hover:border-red-800/60"
          >
            <Trash size={10} weight="bold" /> Clear
          </button>
        )}
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2.5" data-testid="goose-chat-messages">
        {messages.length === 0 ? (
          <p className="text-[11px] text-zinc-600 text-center py-10">No messages yet. Say hi to Silly Goose 🪿</p>
        ) : messages.map(m => {
          const mine = m.email?.toLowerCase() === me?.email?.toLowerCase();
          return (
            <div key={m.id} className="group" data-testid={`goose-msg-${m.id}`}>
              <div className={`flex items-baseline gap-1.5 ${mine ? "justify-end" : ""}`}>
                <span className={`text-[10px] font-medium ${m.is_goose ? "text-amber-300" : mine ? "text-zinc-300" : "text-zinc-400"}`}>
                  {m.author}
                </span>
                {m.is_goose && <span className="text-[8px] px-1 py-0 h-3 border border-amber-500/40 rounded-sm bg-amber-500/15 text-amber-300">SILLY GOOSE 🪿</span>}
                <span className="text-[9px] text-zinc-600">{relativeTime(m.created_at)}</span>
                {isOwner && (
                  <button onClick={() => deleteMsg(m.id)} className="text-[9px] text-zinc-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">✕</button>
                )}
              </div>
              <div className={`${mine ? "text-right" : ""}`}>
                <p className={`inline-block text-[12px] leading-snug px-2.5 py-1.5 rounded-sm max-w-[85%] break-words ${
                  m.is_goose ? "bg-amber-500/10 border border-amber-500/30 text-amber-100"
                  : mine ? "bg-zinc-800/60 text-zinc-100"
                  : "bg-zinc-900/60 border border-zinc-800/60 text-zinc-200"
                }`}>
                  {m.message}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex items-center gap-1.5 p-2 border-t border-zinc-800/60">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={isOwner ? "Reply as Silly Goose..." : "Message Silly Goose..."}
          maxLength={1000}
          data-testid="goose-chat-input"
          className="flex-1 bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
        />
        <button type="submit" disabled={!input.trim() || sending} data-testid="goose-chat-send" className="shrink-0 p-1.5 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40">
          {sending ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneRight size={12} weight="bold" />}
        </button>
      </form>
    </div>
  );
}

export default function SillyGoose() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all / active / closed
  const [tfFilter, setTfFilter] = useState("all"); // all / short / long
  const [editing, setEditing] = useState(null); // null / {} (new) / call obj (edit)
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("calls"); // calls | resources
  const [resTab, setResTab] = useState("books"); // books (expandable)
  const isOwner = user?.email?.toLowerCase() === OWNER_EMAIL;

  useEffect(() => {
    axios.get(`${API}/silly-goose/access`, { withCredentials: true })
      .then(r => setAccess(r.data.allowed === true))
      .catch(() => setAccess(false));
  }, []);

  const fetchCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      const r = await axios.get(`${API}/goose/calls`, { withCredentials: true });
      setCalls(r.data.calls || []);
    } catch (err) {
      console.error("Failed to fetch calls:", err);
    } finally {
      setCallsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (access === true) fetchCalls();
  }, [access, fetchCalls]);

  const saveCall = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.patch(`${API}/goose/calls/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/goose/calls`, payload, { withCredentials: true });
      }
      setEditing(null);
      await fetchCalls();
    } catch (err) {
      console.error("Failed to save call:", err);
      alert(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const deleteCall = async (call) => {
    if (!window.confirm(`Delete ${call.symbol} call? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/goose/calls/${call.id}`, { withCredentials: true });
      setCalls(prev => prev.filter(c => c.id !== call.id));
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  if (access === null) {
    return <div className="p-6 space-y-4 max-w-[1100px]"><Skeleton className="h-10 w-48 bg-zinc-900/60" /><Skeleton className="h-40 bg-zinc-900/40" /></div>;
  }
  if (access === false) return <Navigate to="/profile?tab=gold" replace />;

  const filtered = calls.filter(c => {
    if (filter === "active" && c.status !== "active") return false;
    if (filter === "closed" && c.status === "active") return false;
    if (tfFilter !== "all" && c.timeframe !== tfFilter) return false;
    return true;
  });

  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1300px] overflow-x-hidden" data-testid="silly-goose-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/15 via-zinc-900/40 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-5xl" role="img" aria-label="goose">🪿</span>
          <div className="min-w-0">
            <p className="text-[10px] text-amber-400 uppercase tracking-wider font-bold">Gold Tier · Members Only</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">Silly Goose</h1>
            <p className="text-xs text-zinc-500 mt-1">Long &amp; short-term calls from Silly Goose — and a private chat to talk it through.</p>
          </div>
        </div>
      </div>

      {/* Disclaimer banner — required, visible on every Silly Goose view */}
      <div
        className="rounded-sm border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-3.5 sm:p-4 flex items-start gap-3"
        role="note"
        data-testid="goose-disclaimer-banner"
      >
        <div className="shrink-0 mt-0.5">
          <Warning size={18} weight="fill" className="text-amber-400" />
        </div>
        <p className="text-[11px] sm:text-[12px] leading-relaxed text-zinc-300">
          <span className="text-amber-300 font-semibold">Silly Goose is not a financial advisor.</span>{" "}
          Silly Goose is a patient degen with experience that has been in the markets for over 8 years with over 1,000x returns.
          Silly Goose live calls are no guarantees but is backed with logic and reasoning.{" "}
          <span className="text-amber-300 font-semibold">DYOR.</span>
        </p>
      </div>

      {/* Top-level tabs */}
      <div className="flex items-center gap-1.5 border-b border-zinc-800/80" data-testid="goose-tabs">
        <button
          onClick={() => setTab("calls")}
          data-testid="goose-tab-calls"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px transition-colors ${
            tab === "calls"
              ? "border-amber-500 text-amber-300"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Target size={12} weight="bold" className="inline mr-1 -mt-0.5" /> Live Calls &amp; Chat
        </button>
        <button
          onClick={() => setTab("resources")}
          data-testid="goose-tab-resources"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px transition-colors ${
            tab === "resources"
              ? "border-amber-500 text-amber-300"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Books size={12} weight="bold" className="inline mr-1 -mt-0.5" /> Silly&apos;s Resources
        </button>
        <button
          onClick={() => setTab("portfolio")}
          data-testid="goose-tab-portfolio"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px transition-colors ${
            tab === "portfolio"
              ? "border-amber-500 text-amber-300"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Briefcase size={12} weight="bold" className="inline mr-1 -mt-0.5" /> Portfolio
        </button>
        {isOwner && (
          <button
            onClick={() => setTab("broadcast")}
            data-testid="goose-tab-broadcast"
            className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px transition-colors ${
              tab === "broadcast"
                ? "border-amber-500 text-amber-300"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
            title="Owner only — broadcast email to Gold members"
          >
            <EnvelopeSimple size={12} weight="bold" className="inline mr-1 -mt-0.5" /> Broadcast
          </button>
        )}
      </div>

      {tab === "calls" && (
      /* Top grid: calls (2/3) + chat (1/3) on lg */
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LIVE CALLS */}
        <div className="lg:col-span-2 space-y-3" data-testid="goose-calls-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-heading text-base font-bold text-zinc-100 flex items-center gap-2">
                <Target size={14} weight="bold" className="text-amber-400" /> Live Calls
              </h2>
              <p className="text-[10px] text-zinc-500">Asset calls with entry, targets, and stop-loss. Not financial advice.</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={fetchCalls} className="text-[10px] text-zinc-400 hover:text-zinc-200 px-2 py-1 border border-zinc-800 rounded-sm" data-testid="goose-refresh"><ArrowsClockwise size={11} className={callsLoading ? "animate-spin inline" : "inline"} /></button>
              {isOwner && (
                <button onClick={() => setEditing({})} data-testid="goose-new-call-btn" className="inline-flex items-center gap-1 text-[10px] text-amber-300 bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 px-2 py-1 rounded-sm transition-colors">
                  <Plus size={11} weight="bold" /> New Call
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 text-[10px]">
            {[
              { k: "all", l: "All" },
              { k: "active", l: "Active" },
              { k: "closed", l: "Closed" },
            ].map(f => (
              <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`goose-filter-${f.k}`} className={`px-2 py-0.5 rounded-sm border ${filter === f.k ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>{f.l}</button>
            ))}
            <span className="text-zinc-700">|</span>
            {[
              { k: "all", l: "All timeframes" },
              { k: "short", l: "Short-term" },
              { k: "long", l: "Long-term" },
            ].map(f => (
              <button key={f.k} onClick={() => setTfFilter(f.k)} data-testid={`goose-tf-${f.k}`} className={`px-2 py-0.5 rounded-sm border ${tfFilter === f.k ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>{f.l}</button>
            ))}
          </div>

          {/* Editor */}
          {isOwner && editing && (
            <CallEditor initial={editing} onSave={saveCall} onCancel={() => setEditing(null)} saving={saving} />
          )}

          {/* Calls list */}
          {callsLoading && calls.length === 0 ? (
            <div className="space-y-2"><Skeleton className="h-28 bg-zinc-900/40" /><Skeleton className="h-28 bg-zinc-900/40" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-[11px] text-zinc-600 rounded-sm border border-dashed border-zinc-800 bg-zinc-900/20">
              {calls.length === 0 ? "Silly Goose hasn't posted any calls yet. Check back soon 🪿" : "No calls match those filters."}
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5">
              {filtered.map(c => (
                <CallCard key={c.id} call={c} isOwner={isOwner} onEdit={setEditing} onDelete={deleteCall} />
              ))}
            </div>
          )}
        </div>

        {/* CHAT */}
        <div className="lg:col-span-1">
          <GooseChat isOwner={isOwner} me={user} />
        </div>
      </div>
      )}

      {tab === "portfolio" && (
        <div data-testid="goose-portfolio-section">
          <PortfolioPanel />
        </div>
      )}

      {tab === "resources" && (
        <div className="space-y-4" data-testid="goose-resources-section">
          {/* Sub-tabs */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setResTab("books")}
              data-testid="goose-restab-books"
              className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-sm border transition-colors ${
                resTab === "books"
                  ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                  : "bg-zinc-900/40 border-zinc-800 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <BookOpen size={12} weight="bold" /> Books
            </button>
          </div>

          {resTab === "books" && (
            <div className="space-y-3" data-testid="goose-books-pane">
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="font-heading text-base font-bold text-zinc-100 flex items-center gap-2">
                  <BookOpen size={14} weight="bold" className="text-amber-400" /> Silly&apos;s Reading List
                </h2>
                <span className="text-[10px] text-zinc-500">{SILLYS_BOOKS.length} titles · curated by Silly Goose</span>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed max-w-2xl">
                The mindset, strategy and cashflow books Silly Goose keeps coming back to. Read one, re-read the ones that rattle you.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="goose-books-grid">
                {SILLYS_BOOKS.map((b, i) => (
                  <div
                    key={b.title}
                    data-testid={`goose-book-${i}`}
                    className="group rounded-sm border border-zinc-800 bg-zinc-900/50 p-4 hover:border-amber-500/40 hover:bg-zinc-900/70 transition-colors flex flex-col"
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <div className="shrink-0 w-9 h-12 rounded-sm bg-gradient-to-br from-amber-500/25 to-amber-700/10 border border-amber-500/30 flex items-center justify-center">
                        <BookOpen size={16} weight="bold" className="text-amber-300" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading text-sm font-bold text-zinc-100 leading-tight">{b.title}</h3>
                        <p className="text-[10px] text-zinc-500 mt-0.5">by {b.author}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed flex-1">{b.blurb}</p>
                    <div className="flex items-center justify-between gap-2 mt-3 pt-2.5 border-t border-zinc-800/70">
                      <Badge className="text-[9px] h-4 px-1.5 py-0 border-0 rounded-sm bg-zinc-800 text-zinc-400">
                        {b.tag}
                      </Badge>
                      <a
                        href={amazonLink(b.title, b.author)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`goose-book-link-${i}`}
                        className="inline-flex items-center gap-1 text-[10px] text-amber-300/80 hover:text-amber-300 transition-colors"
                      >
                        Find it <ArrowSquareOut size={10} weight="bold" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "broadcast" && isOwner && (
        <BroadcastPanel />
      )}
    </div>
  );
}
